// Resolving a spoken phrase to a menu entry.
//
// WHY THIS EXISTS AT ALL: there is no menu in VR. `MenuItem.vr` and
// `surfaces: 'vr'` are declared in menu/types.ts but nothing reads them, and
// the DOM chrome the registry builds is not composited into an immersive-vr
// session. So in a headset the rail, the sheet and every icon in them are
// simply absent, and voice is not a convenience — it is the only way to reach
// Sound, Narration, the cinematic tour or the time-of-day.
//
// The index is built FROM THE REGISTRY rather than from a hand-written list,
// which is the whole point: a menu entry added in registry.ts becomes speakable
// with no edit here. What a hand-written list would guarantee instead is the
// failure mode this repo already knows well — a new entry that works on a phone
// and silently does nothing in the headset.
//
// Both dictionaries are indexed at once, always. A visitor in a Quest cannot
// open a language menu they cannot see, so "turn on sound" and "ligar o som"
// have to reach the same entry in the same session.

// Explicit `.ts` on the value import: tests/voice-menu.test.ts loads this
// module under `node --test`, whose resolver adds no extensions. Type-only
// imports are erased before that matters.
import type { Dict, DictKey } from '../i18n/pt-BR';
import type { MenuItem } from './menu/types';
import { normalizePt } from './voiceMatch.ts';

/** One speakable name for a menu entry. */
interface MenuPhrase {
  /** Accent-folded, lowercase. */
  text: string;
  item: MenuItem;
}

/**
 * Extra spoken forms per entry id, in both languages.
 *
 * These are the words people actually use, which are rarely the words on the
 * button: nobody says "narração" for the narrated tour, they say "narrar" or
 * "read it to me". Labels alone would make the feature technically present and
 * practically unusable.
 *
 * Deliberately NOT exhaustive for entries that already have a dedicated intent
 * in voiceCommands (before/after, time of day) — those match earlier and never
 * reach here.
 */
const SPOKEN_ALIASES: Readonly<Record<string, readonly string[]>> = {
  audio: ['som', 'audio', 'sound', 'musica', 'music', 'ambiente sonoro', 'ambient sound'],
  narrator: ['narrar', 'narracao', 'narration', 'narrator', 'guia', 'guide', 'audio guide', 'locucao'],
  autotour: ['tour automatico', 'tour cinematico', 'auto tour', 'cinematic tour', 'passeio', 'walkthrough'],
  sun: ['hora do dia', 'time of day', 'sol', 'sun', 'iluminacao', 'lighting'],
  floorplan: ['planta', 'planta baixa', 'floor plan', 'floorplan', 'ambientes', 'rooms', 'lista de ambientes'],
  dollhouse: ['maquete', 'dollhouse', 'casa de bonecas', 'vista 3d', '3d view'],
  gallery: ['galeria', 'gallery', 'variacoes', 'styles'],
  chat: ['assistente', 'assistant', 'chat', 'ajudante'],
  share: ['link', 'compartilhar', 'share', 'copiar link', 'copy link'],
  qr: ['qr', 'qr code', 'codigo qr'],
  whatsapp: ['whatsapp', 'zap'],
  'saved-session': ['salvar', 'salvar sessao', 'save', 'save session'],
  voice: ['voz', 'voice', 'comando de voz', 'voice command'],
  tilt: ['inclinar', 'tilt'],
  measure: ['medir', 'measure', 'medida', 'distancia', 'distance'],
  note: ['nota', 'note', 'anotacao'],
  report: ['relatorio', 'report', 'pdf'],
  dashboard: ['insights', 'painel', 'dashboard', 'estatisticas'],
};

/**
 * Verbs that mark a phrase as "operate this control" rather than "go here".
 *
 * Their presence is what lets a menu command be matched BEFORE room navigation
 * without stealing from it. "Planta" is both a menu entry and a word a room
 * could be named; "abrir a planta" is unambiguous. A bare label is still
 * matched, but only after rooms have had their turn — see matchMenuCommand's
 * two call sites in voiceCommands.
 */
// NOTE the spelling of the stop verbs: `parar` and `pare`, never `parar?`.
// `parar?` also matches bare "para", which in Portuguese is overwhelmingly the
// preposition "to" — so "ir PARA a cozinha", the single most common navigation
// phrase in the vocabulary, was being read as an action verb and routed to the
// early menu pass instead of to room matching.
const ACTION_VERB =
  /\b(abrir?|abra|abre|ativar?|ative|ativa|ligar?|liga|ligue|desligar?|desliga|desligue|mostrar?|mostra|mostre|exibir?|exibe|fechar?|fecha|feche|iniciar?|inicia|comecar|comeca|parar|pare|tocar?|toca|open|show|turn on|turn off|start|stop|enable|disable|activate|close|play|launch)\b/;

/** True when the phrase carries an explicit "operate a control" verb. */
export function hasActionVerb(text: string): boolean {
  return ACTION_VERB.test(normalizePt(text));
}

/**
 * Build the spoken index for a menu.
 *
 * Both dictionaries are passed rather than the active one: the whole point is
 * that either language reaches the entry. Labels are taken from the catalogue
 * rather than the rendered DOM because in VR there is no rendered DOM.
 */
export function buildMenuVoiceIndex(
  items: readonly MenuItem[],
  dicts: readonly Dict[],
): MenuPhrase[] {
  const phrases: MenuPhrase[] = [];
  const add = (raw: string | undefined, item: MenuItem): void => {
    if (!raw) return;
    const text = normalizePt(raw);
    // One-word-or-shorter labels are noise: a two-character entry would match
    // inside half the words in a sentence.
    if (text.length < 3) return;
    if (phrases.some((p) => p.text === text && p.item.id === item.id)) return;
    phrases.push({ text, item });
  };

  for (const item of items) {
    for (const dict of dicts) add(dict[item.labelKey as DictKey], item);
    for (const alias of SPOKEN_ALIASES[item.id] ?? []) add(alias, item);
  }

  // Longest first so "tour automatico" wins over "tour", and "planta baixa"
  // over "planta". Without this the shorter, vaguer phrase decides.
  return phrases.sort((a, b) => b.text.length - a.text.length);
}

/**
 * Resolve a transcript to a menu entry, or null.
 *
 * `requireVerb` is the caller's way of choosing precedence: with it, only
 * "abrir X" / "open X" matches, which is safe to run before room navigation;
 * without it a bare "som" matches, which is only safe once rooms and variants
 * have already declined.
 */
export function matchMenuCommand(
  text: string,
  index: readonly MenuPhrase[],
  opts: { requireVerb?: boolean } = {},
): MenuItem | null {
  const ntext = normalizePt(text);
  if (opts.requireVerb && !ACTION_VERB.test(ntext)) return null;
  for (const phrase of index) {
    // Word-boundary anchored: "som" must not fire inside "somos" or "assombro".
    if (new RegExp(`(^|\\W)${escapeRegExp(phrase.text)}(\\W|$)`).test(ntext)) return phrase.item;
  }
  return null;
}

/** Outcome of trying to fire an entry, so the caller can say which it was. */
export type MenuActivation = 'activated' | 'not-in-vr' | 'no-control';

/**
 * Fire a menu entry.
 *
 * Two shapes, because the registry is mid-migration: an entry that owns its
 * `activate` is called directly, and a legacy entry is reached by clicking the
 * button its module already listens on. The click works in VR even though the
 * button is invisible there — dispatching does not require the element to be
 * painted, which is what makes voice a substitute for the menu a headset never
 * renders.
 *
 * THE `surfaces` CHECK IS THE POINT of the VR branch, and this is its first
 * real consumer: `MenuSurface` has been declared in menu/types.ts since the
 * menu was built and read by nothing. Without it, "abrir a planta" in a headset
 * clicks a real button, opens a real panel, and shows the visitor nothing —
 * indistinguishable from the command not working, except that it also leaves
 * an invisible panel open. Refusing by declaration is honest and needs no
 * per-entry knowledge here.
 */
export function activateMenuItem(
  item: MenuItem,
  ctx: Parameters<NonNullable<MenuItem['activate']>>[0],
): MenuActivation {
  if (ctx.isXrActive?.() && item.surfaces === 'dom') return 'not-in-vr';
  if (item.activate) {
    void item.activate(ctx);
    return 'activated';
  }
  // `typeof document` rather than a bare reference: the matchers in this module
  // are unit-tested under `node --test`, where there is no DOM, and a missing
  // button and a missing document are the same answer to the caller.
  if (item.legacyDomId && typeof document !== 'undefined') {
    const el = document.getElementById(item.legacyDomId);
    if (el) {
      el.click();
      return 'activated';
    }
  }
  return 'no-control';
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
